import { useTranslation } from "react-i18next";
import { formatPrice } from "@nh/shared";
import type { PackagePrice as ResolvedPackagePrice } from "../packages.catalog";

export interface PackagePriceProps {
  price: ResolvedPackagePrice | null;
  /** Locale de formato de números ("es-MX" / "en-US"). */
  numberLocale: string;
  /** La moneda que la persona pidió en la barra superior. */
  requestedCurrency: string;
}

/**
 * El precio de un paquete: lo que costaría suelto, lo que cuesta, y el
 * ahorro en dinero.
 *
 * ── Ninguno de los tres números se calcula aquí ──────────────────────
 * `listPrices` y `prices` llegan resueltos del servidor, y el ahorro sale
 * de `savedAmount`, que solo los resta. El componente formatea y coloca.
 *
 * ── `formatPrice` o nada ─────────────────────────────────────────────
 * Vive en `@nh/shared` y es la única que sabe que los precios son
 * centavos enteros. Dividir entre 100 en un componente es como se acaba
 * con dos redondeos distintos en dos pantallas y un total que no cuadra
 * con lo que el cliente creyó pagar.
 *
 * ── Sin precio no se pinta un cero ───────────────────────────────────
 * "$0.00" se lee como gratis. Un paquete sin precio en ninguna moneda
 * dice "Precio a consultar", que es lo que de verdad ocurre.
 *
 * ── El tachado es informativo, no decorativo ─────────────────────────
 * `<s>` y no un `line-through` suelto: quien escucha la tarjeta necesita
 * saber que ese importe está cancelado. Y va acompañado de su etiqueta
 * ("Por separado") porque un número tachado sin contexto se confunde con
 * un precio anterior, que aquí no es el caso: es la suma de los productos
 * comprados uno a uno.
 */
export function PackagePrice({
  price,
  numberLocale,
  requestedCurrency,
}: PackagePriceProps) {
  const { t } = useTranslation();

  if (!price) {
    return <p className="text-[15px] font-medium text-ds-ink-3">{t("Precio a consultar")}</p>;
  }

  return (
    <div>
      {price.listCents !== null && price.savedCents !== null && (
        <p className="text-[13px] text-ds-ink-3">
          {t("Por separado")}{" "}
          <s>{formatPrice(price.listCents, price.currency, numberLocale)}</s>
        </p>
      )}

      <p className="mt-1 flex items-baseline gap-2">
        <span className="text-[32px] font-light leading-none tracking-tight text-ds-ink">
          {formatPrice(price.cents, price.currency, numberLocale)}
        </span>
        <span className="text-[12px] font-semibold uppercase tracking-ds-label text-ds-ink-3">
          {price.currency}
        </span>
      </p>

      <p className="mt-1.5 text-[13px] text-ds-ink-3">{t("al mes, se renueva solo")}</p>

      {price.savedCents !== null && (
        <p className="mt-2 text-[14px] font-semibold text-ds-purple">
          {t("Ahorras {{importe}}", {
            importe: formatPrice(price.savedCents, price.currency, numberLocale),
          })}
        </p>
      )}

      {price.isFallbackCurrency && (
        <p className="mt-2 text-[12px] leading-relaxed text-ds-ink-3">
          {t("Este paquete no se vende en {{pedida}}. El precio se muestra en {{usada}}.", {
            pedida: requestedCurrency,
            usada: price.currency,
          })}
        </p>
      )}
    </div>
  );
}
